import { createSelector } from '@reduxjs/toolkit';
import { createPermissionValidator } from '../../utils/permissions';
// ===== BASE SELECTORS =====
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectAccount = (state) => state.auth.account;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectJwtPayload = (state) => state.auth.jwtPayload;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectIsApiConnected = (state) => state.auth.isApiConnected;
export const selectApiStatus = (state) => state.auth.apiStatus;
export const selectError = (state) => state.auth.error;
export const selectLastError = (state) => state.auth.lastError;
// ===== COMPUTED SELECTORS =====
// User role
export const selectUserRole = createSelector([selectUser], (user) => user?.role || null);
// User account ID
export const selectUserAccountId = createSelector([selectUser], (user) => user?.accountId || user?.account?.id || null);
// User features
export const selectUserFeatures = createSelector([selectUser], (user) => user?.features || []);
// User plan
export const selectUserPlan = createSelector([selectUser], (user) => user?.plan || null);
// Permission validator
export const selectPermissionValidator = createSelector([selectJwtPayload], (jwtPayload) => {
    if (!jwtPayload)
        return null;
    return createPermissionValidator(jwtPayload);
});
// Auth tokens object
export const selectTokens = createSelector([selectAccessToken, selectRefreshToken], (accessToken, refreshToken) => {
    if (!accessToken || !refreshToken)
        return null;
    return {
        access_token: accessToken,
        refresh_token: refreshToken,
    };
});
// Is super admin
export const selectIsSuperAdmin = createSelector([selectUserRole], (role) => role === 'superadmin');
// Is admin (admin or superadmin)
export const selectIsAdmin = createSelector([selectUserRole], (role) => role === 'admin' || role === 'superadmin');
// Is moderator (moderator, admin, or superadmin)
export const selectIsModerator = createSelector([selectUserRole], (role) => ['moderator', 'admin', 'superadmin'].includes(role || ''));
// Can see auth debug (admin or superadmin)
export const selectCanSeeAuthDebug = createSelector([selectUserRole], (role) => role === 'admin' || role === 'superadmin');
// ===== PERMISSION SELECTORS =====
// Create a selector factory for role checks
export const createRoleSelector = (requiredRole) => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.hasRole(requiredRole);
});
// Create a selector factory for permission checks
export const createPermissionSelector = (permission) => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.hasPermission(permission);
});
// Create a selector factory for feature checks
export const createFeatureSelector = (feature) => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.hasFeature(feature);
});
// Create a selector factory for resource access checks
export const createAccessSelector = (resource, action = 'read') => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.canAccess(resource, action);
});
// Create a selector factory for user management checks
export const createUserManagementSelector = (targetRole, targetAccountId) => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.canManageUser(targetRole, targetAccountId);
});
// Create a selector factory for same account checks
export const createSameAccountSelector = (targetAccountId) => createSelector([selectPermissionValidator], (validator) => {
    if (!validator)
        return false;
    return validator.isInSameAccount(targetAccountId);
});
// ===== COMMON PERMISSION SELECTORS =====
// Can manage users
export const selectCanManageUsers = createPermissionSelector('users:manage');
// Can manage accounts
export const selectCanManageAccounts = createPermissionSelector('accounts:manage');
// Can view analytics
export const selectCanViewAnalytics = createFeatureSelector('analytics');
// Can view ads
export const selectCanViewAds = createFeatureSelector('ads');
// Can view instagram
export const selectCanViewInstagram = createFeatureSelector('instagram');
// Can access settings
export const selectCanAccessSettings = createAccessSelector('settings', 'read');
// Can access documentation
export const selectCanAccessDocumentation = createAccessSelector('documentation', 'read');
// ===== STATUS SELECTORS =====
// Is loading any auth operation
export const selectIsAuthLoading = createSelector([selectIsLoading], (isLoading) => isLoading);
// Has auth error
export const selectHasAuthError = createSelector([selectError], (error) => !!error);
// Is ready (initialized and not loading)
export const selectIsAuthReady = createSelector([selectIsInitialized, selectIsLoading], (isInitialized, isLoading) => isInitialized && !isLoading);
// Auth status summary
export const selectAuthStatus = createSelector([selectIsAuthenticated, selectIsLoading, selectIsInitialized, selectError], (isAuthenticated, isLoading, isInitialized, error) => ({
    isAuthenticated,
    isLoading,
    isInitialized,
    hasError: !!error,
    isReady: isInitialized && !isLoading,
}));
// API connection status
export const selectApiConnectionStatus = createSelector([selectIsApiConnected, selectApiStatus], (isConnected, status) => ({
    isConnected,
    status,
    isChecking: status === 'checking',
    isMock: status === 'mock',
}));
